import React, { useCallback, useEffect, useMemo, useState } from "react";
import "devextreme/dist/css/dx.light.css";
import { Button } from "devextreme-react/button";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import List, { SearchEditorOptions } from "devextreme-react/list";
import { DateBox } from "devextreme-react/date-box";
import notify from "devextreme/ui/notify";
import PropTypes from "prop-types";
import { ScrollView } from "devextreme-react/scroll-view";
import "./SubscribeResourceToMultipleOrgs.scss";
import classNames from "classnames";
import { useUser } from "../../contexts/UserContext";
import { ResourceVersionHeaderInfoSVG } from "../../Assets/SVGs/ResourceSVGs";
import { checkListSvg, stickyNoteSvg } from "../../Assets/SVGs/organizationSVGs";
import { closeIconSVG } from "../../Assets/SVGs/environmentSVGs";
import { orgDataPropTypes, subscriptionPropTypes } from "../../Constants/propTypes";

const toastConfig = {
  width: 300,
  position: "top",
  displayTime: 3000,
};
const SubscribeResourceToMultipleOrgs = ({
  visible,
  onHidingCallback,
  resourceVersionDisplay,
  resourceVersionId,
  isInfoButtonVisible = false,
  currentlySubscribedOrgs,
  clinithinkChildOrg = true,
  orgData,
  onSubscriptionSuccessCallback,
  checkResourcesType,
  clinithinkSubscriptionRange,
}) => {
  const { backend } = useUser();
  const todayDate = useMemo(() => new Date(), []);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [dates, setDates] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [isClinithinkOrg, setIsClinithinkOrg] = useState(false);
  const [parentSubscriptionRange, setParentSubscriptionRange] = useState([]);

  const clinithinkOrgData = useMemo(
    () => ({
      id: 1,
      name: "Clinithink",
      disabled: true,
    }),
    []
  );

  const allResDetailsObj = useMemo(
    () => ({
      pageNumber: 1,
      pageSize: 10,
      resourceType: null,
      resourceName: "",
    }),
    []
  );

  // Updated orgData with the disabled property
  const updatedOrgData = useMemo(() => {
    if (orgData) {
      return { ...orgData, disabled: true };
    }
    return null;
  }, [orgData]);

  useEffect(() => {
    if (updatedOrgData && !selectedOrgs.length && checkResourcesType === "available") {
      setSelectedOrgs([updatedOrgData]);
      setDates({ [updatedOrgData.name]: { start: todayDate, end: null } });
    }
  }, [updatedOrgData, selectedOrgs, todayDate, checkResourcesType]);

  const callParentsOrgSubDates = useCallback(
    (id) => {
      // fetches parents subscription dates to add validation for their respective childrens
      backend
        .callParentsOrgSubscriptionDates(id, resourceVersionId, allResDetailsObj)
        .then((res) => {
          if (res?.data?.resources?.length > 0) {
            setParentSubscriptionRange(res?.data?.resources[0]);
          } else {
            setParentSubscriptionRange([]); // No resources found
          }
        })
        .catch((error) => {
          if (error.response) {
            // setErrorMessage('Failed to fetch subscription dates.');
            console.error("Response data:", error.response.data);
            console.error("Response status:", error.response.status);
          } else {
            // Something else happened
            console.error("Error message:", error.message);
          }
          // Set the subscription range to an empty array
          setParentSubscriptionRange([]);
        });
    },
    [backend, allResDetailsObj, resourceVersionId]
  );

  const organizationChildsWithoutSubscription = useCallback(() => {
    // fetches childrens which does not have subscription
    backend
      .callOrganizationsChildsWithoutSubscription(updatedOrgData?.id, resourceVersionId)
      .then((res) => {
        if (res?.data) {
          const updatedOrganizations =
            checkResourcesType === "subscribed"
              ? [...res.data]
              : [updatedOrgData, ...res.data];
          setOrganizations(updatedOrganizations);
        } else {
          setOrganizations([]);
        }
      })
      .catch((error) => {
        if (error.response) {
          // setErrorMessage('Failed to fetch organizations without subscription.');
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
        } else {
          // Something else happened
          console.error("Error message:", error.message);
        }
      });
    if (updatedOrgData?.id && checkResourcesType !== "available") {
      // fetches dates for the present Org to subscribe for child org from 2nd time
      callParentsOrgSubDates(updatedOrgData?.id);
    } else if (updatedOrgData?.parentId && checkResourcesType === "available") {
      // fetches dates of the parent Org when we are subscribing for the 1st time
      callParentsOrgSubDates(updatedOrgData?.parentId);
    } else {
      setParentSubscriptionRange([]);
    }
    setSelectedOrgs([]);
  }, [
    backend,
    resourceVersionId,
    updatedOrgData,
    checkResourcesType,
    callParentsOrgSubDates,
  ]);

  useEffect(() => {
    if (visible && !clinithinkChildOrg) {
      organizationChildsWithoutSubscription();
    }
  }, [visible, organizationChildsWithoutSubscription, clinithinkChildOrg]);

  useEffect(() => {
    if (clinithinkChildOrg) {
      // fetches clinithink childrens for resource Tab
      backend.getPartnerOrgs().then((res) => {
        if (res?.data) {
          const existingOrganizationNames = new Set(
            currentlySubscribedOrgs?.map((org) => org.organizationName)
          );
          const filteredOrganizations =
            res?.data.filter((org) => !existingOrganizationNames?.has(org.name)) || [];
          // Check if clinithink is populated in the list
          const hasClinithinkOrgData = existingOrganizationNames.has(
            clinithinkOrgData?.name
          );
          setIsClinithinkOrg(!hasClinithinkOrgData);
          const updatedOrganizations = hasClinithinkOrgData
            ? [...filteredOrganizations]
            : [clinithinkOrgData, ...filteredOrganizations];
          setOrganizations(updatedOrganizations);
          if (!hasClinithinkOrgData) {
            setSelectedOrgs([clinithinkOrgData]);
            setDates({ [clinithinkOrgData.name]: { start: todayDate, end: null } });
          }
        } else {
          setOrganizations([]);
        }
      });
    }
  }, [
    backend,
    currentlySubscribedOrgs,
    clinithinkChildOrg,
    updatedOrgData,
    checkResourcesType,
    clinithinkOrgData,
    todayDate,
  ]);

  const handleClearSelectedOrg = useCallback(() => {
    // Handle the condition based on isClinithinkOrg and clinithinkChildOrg
    if (isClinithinkOrg === true) {
      setSelectedOrgs([clinithinkOrgData]);
      setDates({ [clinithinkOrgData.name]: { start: todayDate, end: null } });
    } else {
      setSelectedOrgs([]);
      setDates({});
    }
  }, [todayDate, clinithinkOrgData, isClinithinkOrg]);

  const handleRemoveSingleOrg = useCallback(
    (org) => {
      setSelectedOrgs(selectedOrgs?.filter((item) => item?.id !== org?.id));
    },
    [selectedOrgs]
  );

  const handleSelectionChange = useCallback(
    (e) => {
      const { addedItems, removedItems } = e;

      // Get unique items from the list
      const getUniqueItems = (items) => {
        const orgItems = new Set();
        return items.filter((item) => {
          if (orgItems.has(item.id)) return false;
          orgItems.add(item.id);
          return true;
        });
      };

      // Filter out removed items
      const filterRemovedItems = (items, discardedItems) => {
        const removedTexts = new Set(discardedItems.map((item) => item.name));
        return items.filter((item) => !removedTexts.has(item.name));
      };

      // Update selected organizations
      const updateSelectedOrgs = (prevSelected) => {
        const newSelected = [...prevSelected, ...addedItems];
        const uniqueItems = getUniqueItems(newSelected);
        return filterRemovedItems(uniqueItems, removedItems);
      };

      // Create a new dates object with added organizations
      const addNewOrgs = (existingDates, itemsToAdd) =>
        itemsToAdd.reduce(
          (updatedDates, item) => {
            // Create a new object based on the existing dates
            const newDates = { ...updatedDates };
            if (!newDates[item.name]) {
              newDates[item.name] = { start: todayDate, end: null };
            }
            return newDates;
          },
          { ...existingDates }
        );

      // Create a new dates object with removed organizations
      const removeUnselectedOrgs = (existingDates, itemsToRemove) => {
        const removedTexts = new Set(itemsToRemove.map((item) => item.name));
        return Object.keys(existingDates).reduce((updatedDates, key) => {
          // Create a new object to avoid modifying existingDates
          const newDates = { ...updatedDates };
          if (!removedTexts.has(key)) {
            newDates[key] = existingDates[key];
          }
          return newDates;
        }, {});
      };

      // Update dates state
      const updateDates = (prevDates) => {
        const datesAfterAddition = addNewOrgs(prevDates, addedItems);
        return removeUnselectedOrgs(datesAfterAddition, removedItems);
      };

      setSelectedOrgs((prevSelected) => updateSelectedOrgs(prevSelected));
      setDates((prevDates) => updateDates(prevDates));
    },
    [todayDate]
  );

  const handleDateChange = useCallback((org, type, value) => {
    setDates((prevDates) => ({
      ...prevDates,
      [org.name]: {
        ...prevDates[org.name],
        [type]: value,
      },
    }));
  }, []);

  const createPayload = useCallback(
    () => ({
      resourceSubscriptionList: selectedOrgs?.map((org) => {
        const start = new Date(dates[org?.name]?.start);
        const end = new Date(dates[org?.name]?.end);
        // Adjust end date to ensure it covers the full day
        // by setting the end time to the start of the next day in UTC
        end.setUTCDate(end.getUTCDate() + 1);

        return {
          id: resourceVersionId,
          resourceVersionId,
          organizationId: org.id,
          subscriptionStart: start.toISOString(),
          subscriptionEnd: end.toISOString(),
        };
      }),
    }),
    [dates, resourceVersionId, selectedOrgs]
  );

  const findValidOrgForAutoFill = useCallback(
    () =>
      selectedOrgs.find((org) => {
        const orgDates = dates[org?.name];
        return orgDates?.start && orgDates?.end;
      }),
    [dates, selectedOrgs]
  );

  const handleAutoFillDates = useCallback(() => {
    const validOrg = findValidOrgForAutoFill();
    if (validOrg) {
      const newDates = { ...dates };
      selectedOrgs.forEach((org) => {
        newDates[org?.name] = { ...dates[validOrg?.name] };
      });
      setDates(newDates);
    }
  }, [dates, findValidOrgForAutoFill, selectedOrgs]);

  const handleSubscribe = useCallback(() => {
    const payload = createPayload();
    const isValidPayload = payload.resourceSubscriptionList.every((item) => {
      const startDate = new Date(item?.subscriptionStart);
      const endDate = new Date(item?.subscriptionEnd);

      // Add one day to the end date
      endDate.setDate(endDate.getDate() + 1);

      return startDate && endDate && startDate < endDate;
    });

    if (!isValidPayload) {
      notify(
        {
          message:
            "Please ensure all start dates are before end dates and all dates are valid.",
          width: toastConfig.width,
          position: toastConfig.position,
        },
        "error",
        toastConfig.displayTime
      );
      return;
    }
    backend
      .createMultiOrgSubscriptionForResource(payload)
      .then(() => {
        if (checkResourcesType) {
          onSubscriptionSuccessCallback();
        }
        onHidingCallback();
        notify(
          {
            message: "Success",
            width: toastConfig.width,
            position: toastConfig.position,
          },
          "success",
          toastConfig.displayTime
        );
        setSelectedOrgs([]);
      })
      .catch((err) => {
        notify(
          {
            message: err?.response?.data?.message,
            width: toastConfig.width,
            position: toastConfig.position,
          },
          "error",
          toastConfig.displayTime
        );
      });
  }, [
    backend,
    createPayload,
    onHidingCallback,
    onSubscriptionSuccessCallback,
    checkResourcesType,
  ]);

  const allOrgsHaveDates = useCallback(() => {
    // Find the organization matching the updatedOrgDataId i.e parentOrg
    const parentOrg =
      checkResourcesType === "available"
        ? selectedOrgs.find((org) => org?.id === updatedOrgData?.id)
        : null;

    // If checkResourcesType is "available" and no parentOrg is found, return false
    if (checkResourcesType === "available" && !parentOrg) {
      return false;
    }

    // If parentOrg exists, get its dates
    const parentOrgDates = parentOrg ? dates[parentOrg?.name] : null;

    // Convert parentOrg end date to Date object and add one day
    const parentEndDate = parentOrgDates ? new Date(parentOrgDates.end) : null;
    if (parentEndDate) {
      parentEndDate.setDate(parentEndDate.getDate() + 1);
    }

    return selectedOrgs.every((org) => {
      const orgDates = dates[org?.name];

      // Convert start and end to Date objects
      const startDate = new Date(orgDates?.start);
      const endDate = new Date(orgDates?.end);

      // Add one day to the organization's end date
      endDate.setDate(endDate.getDate() + 1);

      return (
        startDate &&
        endDate &&
        startDate < endDate &&
        (parentOrgDates
          ? startDate >= new Date(parentOrgDates.start) && endDate <= parentEndDate
          : true)
      );
    });
  }, [selectedOrgs, dates, updatedOrgData, checkResourcesType]);

  const autoFillEnabled = useCallback(() => {
    // Ensure there are multiple selections
    if (selectedOrgs.length <= 1) return false;

    let validDatesFound = false;
    let consistentDates = true;
    let referenceStartDate = null;
    let referenceEndDate = null;

    selectedOrgs.forEach((org) => {
      const orgDates = dates[org.name];
      // Skip orgs without complete date info
      if (!orgDates?.start || !orgDates?.end) return;

      // Mark that we found at least one org with valid dates
      validDatesFound = true;

      if (!referenceStartDate && !referenceEndDate) {
        // For the first org with valid dates, set reference dates
        referenceStartDate = orgDates.start;
        referenceEndDate = orgDates.end;
      } else if (
        // Directly check if subsequent orgs with dates match the reference dates
        orgDates?.start?.getTime() !== referenceStartDate?.getTime() ||
        orgDates?.end?.getTime() !== referenceEndDate?.getTime()
      ) {
        consistentDates = false;
      }
    });

    // Enable button if valid dates are found and all dates are consistent
    return validDatesFound && consistentDates;
  }, [dates, selectedOrgs]);

  const onManualDateEntry = useCallback((e) => {
    e.component.option("inputAttr", { readonly: true });
  }, []);

  const renderOrgs = useCallback(
    () => (
      <div className="subscribe-orgs">
        <div className="org-list">
          <div className="select-orgs-to-subscribe">
            <h6>Select organizations to subscribe</h6>
            <List
              dataSource={organizations}
              selectionMode="all"
              displayExpr="name"
              showSelectionControls={true}
              onSelectionChanged={handleSelectionChange}
              height={300}
              searchExpr="name"
              searchEnabled={true}
              noDataText="Organizations already subscribed"
              selectedItems={selectedOrgs}
              pageLoadMode="all"
            >
              <SearchEditorOptions placeholder="Search for an organization" />
            </List>
          </div>
        </div>
        <div className="vr-line" />
        <div className="selected-orgs">
          <div className="selected-orgs-list">
            <div className="selected-orgs-container">
              <div className="selected-orgs-container-header">
                <h6>Selected organizations</h6>
                {isInfoButtonVisible && (
                  <div className="button-container">
                    <span>{ResourceVersionHeaderInfoSVG(true)}</span>
                    <div className="infoPopover-content">
                      <div>{stickyNoteSvg} Note</div>

                      <div>
                        The subscription dates set for your organization constrain the
                        dates that you can select for organizations you manage. You can
                        change the dates, but the selection must be within the start and
                        end date range for your organization.
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                className="selectedOrgClear"
                onClick={handleClearSelectedOrg}
                type="button"
              >
                Clear all
              </button>
            </div>
            <hr className="horizontal-line" />
            <div className={classNames("selected-org-headers", "subscriptionPopup")}>
              <h4 className="organization-text">Organizations</h4>
              <h4>Start Date</h4>
              <h4>End Date</h4>
            </div>
            {selectedOrgs.length > 0 && (
              <ScrollView height={270} direction="vertical">
                <div>
                  {selectedOrgs.map((org, index) => (
                    <div className="selected-orgs-list-items" key={org?.name}>
                      <div className={classNames("date-boxes", "closeIconToGrid")}>
                        <div className="label-name">{org?.name}</div>
                        <DateBox
                          value={dates[org?.name]?.start || todayDate}
                          onValueChanged={(e) => handleDateChange(org, "start", e.value)}
                          displayFormat="MMM dd, yyyy"
                          placeholder="Start date"
                          min={todayDate}
                          max={
                            clinithinkSubscriptionRange?.subscriptionEnd ??
                            parentSubscriptionRange?.endDate ??
                            null
                          }
                          maxWidth={150}
                          required={true}
                          className="sub-start-date"
                          onContentReady={onManualDateEntry} // to disable manual date entry
                        />
                        <DateBox
                          value={dates[org?.name]?.end}
                          onValueChanged={(e) => handleDateChange(org, "end", e.value)}
                          displayFormat="MMM dd, yyyy"
                          placeholder="End date"
                          min={dates[org?.name]?.start || todayDate}
                          max={
                            clinithinkSubscriptionRange?.subscriptionEnd ??
                            parentSubscriptionRange?.endDate ??
                            null
                          }
                          maxWidth={150}
                          className="sub-end-date"
                          onContentReady={onManualDateEntry} // to disable manual date entry
                        />
                        {!(
                          (checkResourcesType === "available" && index === 0) ||
                          org?.name === "Clinithink"
                        ) && (
                          <button
                            className="closeIcon"
                            onClick={() => handleRemoveSingleOrg(org)}
                            type="button"
                          >
                            {closeIconSVG}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollView>
            )}
            {selectedOrgs.length === 0 && (
              <div className="empty-results">
                <div>
                  <div>{checkListSvg}</div>
                  <div className="empty-results-text">
                    Select organization from the list to set the start and the end Date
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      dates,
      handleDateChange,
      handleSelectionChange,
      organizations,
      todayDate,
      selectedOrgs,
      isInfoButtonVisible,
      handleRemoveSingleOrg,
      checkResourcesType,
      handleClearSelectedOrg,
      clinithinkSubscriptionRange?.subscriptionEnd,
      parentSubscriptionRange?.endDate,
      onManualDateEntry,
    ]
  );

  return (
    <Popup
      visible={visible}
      showCloseButton={true}
      title={resourceVersionDisplay}
      width={800}
      height={500}
      contentRender={renderOrgs}
      wrapperAttr={{ class: "orgs-subscription-popup" }}
      className="modal-popup"
      onHiding={onHidingCallback}
    >
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        render={() => (
          <Button
            text="Auto fill dates"
            className="subscribe-btn"
            onClick={handleAutoFillDates}
            disabled={!autoFillEnabled()}
            hint="Given that at least one selected organisation has a valid start and end date, apply that start and end to all"
          />
        )}
      />
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        render={() => (
          <Button
            text="Subscribe"
            className="subscribe-btn"
            onClick={handleSubscribe}
            disabled={!allOrgsHaveDates() || selectedOrgs.length === 0}
          />
        )}
      />
    </Popup>
  );
};

SubscribeResourceToMultipleOrgs.propTypes = {
  onHidingCallback: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  resourceVersionDisplay: PropTypes.string.isRequired,
  resourceVersionId: PropTypes.string,
  clinithinkChildOrg: PropTypes.bool,
  isInfoButtonVisible: PropTypes.bool,
  currentlySubscribedOrgs: PropTypes.arrayOf(subscriptionPropTypes),
  orgData: orgDataPropTypes,
  onSubscriptionSuccessCallback: PropTypes.func,
  checkResourcesType: PropTypes.string,
  clinithinkSubscriptionRange: PropTypes.shape({
    subscriptionStart: PropTypes.string.isRequired,
    subscriptionEnd: PropTypes.string.isRequired,
  }).isRequired,
};

export default SubscribeResourceToMultipleOrgs;
